import { Flex, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { NumberInputControl, SelectControl } from "formik-chakra-ui";
import React from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import {
  isDecimalNumberFormat,
  NUMBER_TYPE_FORMAT_DECIMAL,
  NUMBER_TYPE_FORMAT_DECIMAL_NO_COMMAS,
  NUMBER_TYPE_FORMAT_WHOLE,
  NUMBER_TYPE_FORMAT_WHOLE_NO_COMMAS,
} from "src/constants/numberFormat";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import * as QuestionType from "../QuestionType";

type Props = {
  questionId: uuid;
};
export const NumberFormat: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();

  const selectedFormatOption = options.find(
    (o) => o.value === formik.values.numberFormat
  );

  React.useEffect(() => {
    (async () => {
      if (formik.values.type === AF.V3.QuestionTypes.Number) {
        await formik.setFieldValue(
          "numberFormat",
          formik.values.numberFormat ?? NUMBER_TYPE_FORMAT_WHOLE
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.type]);

  return (
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="2">
        <SelectControl
          label="Number type"
          name="numberFormat"
          selectProps={{
            onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
              formik.handleChange(event);
              const formatVal = event.target.value;
              void formik.setFieldValue(
                "numberPrecision",
                isDecimalNumberFormat(formatVal)
                  ? Math.max(Number(formik.values.numberPrecision) || 1, 1)
                  : 0
              );
            },
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectControl>
        <Text fontSize="sm" textColor="gray.600">
          {selectedFormatOption?.description}
        </Text>
      </Flex>
      {isDecimalNumberFormat(formik.values.numberFormat) && (
        <Flex direction="column" gap="2">
          <NumberInputControl
            name="numberPrecision"
            label="Decimal places"
            numberInputProps={{
              min: 1,
              max: 6,
            }}
          />
          <Text fontSize="sm" textColor="gray.600">
            The number of decimal places this number can have. Must be at least
            1 and can be up to 6.
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export const NumberConstraints: React.FC<Props> = ({ questionId }) => {
  const formik = useFormikContext<FormikType>();

  if (formik.values.numberConstraints.switch === "off") {
    return null;
  }

  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" gap="4">
        <NumberInputControl
          name="numberConstraints.minValue"
          label="Minimum (optional)"
          showStepper={false}
          labelProps={{ htmlFor: `numberConstraints.minValue-${questionId}` }}
          numberInputProps={{ id: `numberConstraints.minValue-${questionId}` }}
        />
        <NumberInputControl
          name="numberConstraints.maxValue"
          label="Maximum (optional)"
          showStepper={false}
          labelProps={{ htmlFor: `numberConstraints.maxValue-${questionId}` }}
          numberInputProps={{ id: `numberConstraints.maxValue-${questionId}` }}
        />
      </Flex>
      <Text fontSize="sm" textColor="gray.600">
        The parent will only be able to enter a number between the specified
        minimum and maximum.
      </Text>
    </Flex>
  );
};

export const NumberConstraintsSwitch: React.FC<Props> = ({ questionId }) => {
  return (
    <FormikSwitchableContent<"on" | "off">
      id={`numberConstraintsSwitch${questionId}`}
      label="Constraints"
      name="numberConstraints.switch"
      checked={["on", ""]}
      unchecked={["off", ""]}
      showContent={(value) => value === "on"}
      description=""
    >
      <NumberConstraints questionId={questionId} />
    </FormikSwitchableContent>
  );
};

const z_coerced_number = z.coerce.number().nullable();

export const FormikSchema = z
  .object({
    numberFormat: z
      .union([
        z.literal(NUMBER_TYPE_FORMAT_WHOLE),
        z.literal(NUMBER_TYPE_FORMAT_DECIMAL),
        z.literal(NUMBER_TYPE_FORMAT_WHOLE_NO_COMMAS),
        z.literal(NUMBER_TYPE_FORMAT_DECIMAL_NO_COMMAS),
      ])
      .optional(),
    numberPrecision: z_coerced_number.optional(),
    numberConstraints: z.object({
      switch: z.union([z.literal("on"), z.literal("off")]),
      minValue: z_coerced_number.optional(),
      maxValue: z_coerced_number.optional(),
    }),
  })
  .and(QuestionType.FormikSchema)
  .superRefine((data, ctx) => {
    const { type, numberFormat, numberPrecision, numberConstraints } = data;
    const { switch: switchValue, minValue, maxValue } = numberConstraints;

    // Validate that minValue is less than maxValue, only if both are defined (not null or undefined)
    if (
      switchValue === "on" &&
      isNotNull(minValue) &&
      isNotNull(maxValue) &&
      minValue >= maxValue
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Minimum must be less than maximum",
        path: ["numberConstraints", "minValue"], // The field that causes the issue
      });
    }

    if (
      type === AF.V3.QuestionTypes.Number &&
      isDecimalNumberFormat(numberFormat) &&
      (isNaN(Number(numberPrecision)) ||
        numberPrecision === undefined ||
        numberPrecision === null ||
        numberPrecision < 1 ||
        numberPrecision > 6)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Decimal places must be between 1 and 6",
        path: ["numberPrecision"], // The field that causes the issue
      });
    }
  });

export type FormikType = z.infer<typeof FormikSchema>;

type FormatOptions = {
  label: string;
  value: string;
  description: string;
};

const options: FormatOptions[] = [
  {
    label: "Whole (without commas)",
    value: NUMBER_TYPE_FORMAT_WHOLE_NO_COMMAS,
    description:
      "The number entered cannot have any decimal points and will display without comma separators. Leading zeros are removed.",
  },
  {
    label: "Whole (with commas)",
    value: NUMBER_TYPE_FORMAT_WHOLE,
    description:
      "The number entered cannot have any decimal points. Leading zeros are removed.",
  },
  {
    label: "Decimal (without commas)",
    value: NUMBER_TYPE_FORMAT_DECIMAL_NO_COMMAS,
    description:
      "The number entered can have a number of decimal places as specified below, and will display without comma separators. Leading zeros are removed.",
  },
  {
    label: "Decimal (with commas)",
    value: NUMBER_TYPE_FORMAT_DECIMAL,
    description:
      "The number entered can have a number of decimal places as specified below. Leading zeros are removed.",
  },
];
